import invariant from 'invariant';
import path from "ramda/es/path";
import T from "ramda/es/T";
import assoc from "ramda/es/assoc";
import compose from "ramda/es/compose";
import concat from "ramda/es/concat";
import includes from "ramda/es/includes";
import init from "ramda/es/init";
import length from "ramda/es/length";
import map from "ramda/es/map";
import o from "ramda/es/o";
import sortBy from "ramda/es/sortBy";
import times from "ramda/es/times";
import unnest from "ramda/es/unnest";
import isNotNil from "ramda-extension/es/isNotNil";
import isObject from "ramda-extension/es/isObject";
import isString from "ramda-extension/es/isString";
import rejectNil from "ramda-extension/es/rejectNil";
import { getFormData, getFormStream } from '../../stream/streamStorage';
import { createFieldRecord, getArrayDeepPath, getArrayLocation, getArrayNames, getDeepPath } from '../../utils/data';
import { invariantFieldArray } from '../../utils/invariant';
import { arrayDeepLevel, fieldType, targetIndicesLevel } from '../constants';
import { getNotPresentValidationTypes, getPristineValidationTypes, getValidationPriority, getValidationTypes } from '../globalConfig';
const mapUnnest = compose(unnest, map);
const sortByPriority = sortBy(({ type })=>getValidationPriority()[type]);
const expandArray = ({ name, arrayLevel, index, validatePristine, targetIndices = [] }, fieldIndices, formDescriptor, formData)=>{
    const dependentFieldDescriptor = {
        ...formDescriptor[name],
        validateDependentPristine: validatePristine
    };
    switch(arrayLevel){
        case arrayDeepLevel.SAME_INDEX:
            invariant(isNotNil(fieldIndices), `You specify SAME_INDEX for ${name} but no fieldIndices were provided`);
            return [
                [
                    name,
                    fieldIndices,
                    dependentFieldDescriptor
                ]
            ];
        case arrayDeepLevel.PROVIDED_INDEX:
            invariant(isNotNil(index), `You specify PROVIDED_INDEX for ${name} but no index were provided`);
            return [
                [
                    name,
                    [
                        index
                    ],
                    dependentFieldDescriptor
                ]
            ];
        case arrayDeepLevel.WHOLE:
            const nestedArrayIndices = init(fieldIndices || []);
            const arrayDeepPath = getArrayDeepPath(formDescriptor, dependentFieldDescriptor.array, nestedArrayIndices);
            const arrayLength = length(path(arrayDeepPath, formData));
            if (!arrayLength) {
                return [];
            }
            return times((n)=>[
                    name,
                    [
                        ...nestedArrayIndices,
                        n
                    ],
                    dependentFieldDescriptor
                ], arrayLength);
        case arrayDeepLevel.PARENT_ROW:
            invariant(fieldIndices?.length >= 2, `You specify PARENT_ROW on a non-nested field`);
            return [
                [
                    name,
                    init(fieldIndices),
                    dependentFieldDescriptor
                ]
            ];
        case arrayDeepLevel.TARGET_WHOLE:
            // NOTE: TARGET_WHOLE is suppose to be used for target any array outside of array validations
            // you can provide indices directly or if the array is nested than it will try to take these indices
            const targetArrayIndices = targetIndices === targetIndicesLevel.NESTED ? fieldIndices : targetIndices;
            const arrayLocation = getArrayLocation(formDescriptor, dependentFieldDescriptor.array);
            invariant(arrayLocation.length - 1 === targetArrayIndices.length, `Nested level (${arrayLocation}) of target array does not match with provided indices (${targetArrayIndices})`);
            const targetArrayDeepPath = getArrayDeepPath(formDescriptor, dependentFieldDescriptor.array, targetArrayIndices);
            const targetArrayLength = length(path(targetArrayDeepPath, formData));
            if (!targetArrayLength) {
                return [];
            }
            return times((n)=>[
                    name,
                    [
                        ...targetArrayIndices,
                        n
                    ],
                    dependentFieldDescriptor
                ], targetArrayLength);
        default:
            invariant(false, `provided arrayLevel ${arrayLevel} is missing or it is invalid`);
    }
};
const getDependentFieldsForDescriptors = (descriptorForFields, formDescriptor, formData)=>mapUnnest(([, fieldIndices, fieldDescriptor])=>mapUnnest((dependentField)=>{
            invariant(isObject(dependentField) || isString(dependentField), `provided dependent field ${dependentField} is not valid type`);
            let resolvedDependentField = dependentField;
            if (isObject(dependentField)) {
                if (dependentField.name && dependentField.arrayLevel) {
                    return expandArray(dependentField, fieldIndices, formDescriptor, formData);
                } else if (dependentField.name) {
                    resolvedDependentField = dependentField.name;
                } else {
                    throw new Error(`provided dependent field ${dependentField} is missing 'name' or more properties`);
                }
            }
            const dependentFieldDescriptor = {
                ...formDescriptor[resolvedDependentField],
                validateDependentPristine: dependentField.validatePristine
            };
            invariant(dependentFieldDescriptor !== undefined, `provided dependent field ${resolvedDependentField} has not been found. You need to specify existing field`);
            invariant(dependentFieldDescriptor.type !== fieldType.FIELD_ARRAY, `provided dependent field ${resolvedDependentField} has array type. You need to specify arrayLevel`);
            return [
                [
                    resolvedDependentField,
                    undefined,
                    dependentFieldDescriptor
                ]
            ];
        }, fieldDescriptor.dependentFields || []), descriptorForFields);
const hasInSomeGroup = ({ type, fieldRecord, formFieldState, dirtyGroup, pristineGroup, notPresentGroup })=>{
    const fieldStateRecord = formFieldState[fieldRecord];
    const isRegistered = !!fieldStateRecord;
    const isPristine = fieldStateRecord?.pristine;
    if (!isRegistered && includes(type, notPresentGroup)) {
        return true;
    } else if (isRegistered && isPristine && includes(type, pristineGroup)) {
        return true;
    } else if (isRegistered && !isPristine && includes(type, dirtyGroup)) {
        return true;
    } else {
        return false;
    }
};
const expandWithFormState = (formDescriptor, formFieldState)=>map((val)=>{
        const { fieldRecord, fieldName, fieldIndices } = val;
        const { type, array } = formDescriptor[fieldName];
        const { arrayPlacementPath, arrayDeepPath, section } = formFieldState[fieldRecord] || {};
        const isFieldArray = type === fieldType.FIELD_ARRAY;
        return {
            ...val,
            arrayDeepPath: isFieldArray ? arrayDeepPath || getArrayDeepPath(formDescriptor, array, init(fieldIndices)) : undefined,
            arrayPlacementPath: isFieldArray ? arrayPlacementPath || getArrayNames(formDescriptor, array, init(fieldIndices)) : undefined,
            section
        };
    });
export const resolveValidationDescriptors = ({ runType, formData, reduxStore, formDescriptor, fieldsToValidate, formFieldState = {}, expandDependent = true })=>{
    const descriptorForFields = map(([fieldName, fieldIndices])=>{
        const fieldDescriptor = formDescriptor[fieldName];
        invariant(fieldDescriptor, `cannot find descriptor for fieldName: ${fieldName}`);
        invariantFieldArray(fieldDescriptor, fieldName, fieldIndices);
        return [
            fieldName,
            fieldIndices,
            fieldDescriptor
        ];
    }, fieldsToValidate);
    const dependentFields = expandDependent ? getDependentFieldsForDescriptors(descriptorForFields, formDescriptor, formData) : [];
    const concatedDescriptors = concat(descriptorForFields, dependentFields);
    return map(([fieldName, fieldIndices, { when = T, validations, asyncValidations, validateDependentPristine, ...fieldDescriptor }])=>{
        const fieldRecord = createFieldRecord(fieldName, fieldIndices);
        const validationParams = {
            fieldValue: path(getDeepPath(formDescriptor, fieldDescriptor, fieldIndices), formData),
            fieldIndices,
            fieldName,
            fieldRecord,
            formData,
            formDescriptor,
            fieldDescriptor,
            formFieldState,
            reduxState: reduxStore?.getState(),
            reduxDispatch: reduxStore?.dispatch,
            streamStorage: {
                getFormStream,
                getFormData
            },
            runType
        };
        return {
            when,
            validations,
            fieldRecord,
            asyncValidations,
            validationParams,
            validateDependentPristine
        };
    })(concatedDescriptors);
};
const createValidationResult = ({ dirtyGroup, fieldRecord, pristineGroup, formFieldState, notPresentGroup, validationParams })=>o(rejectNil, map(({ type, when, validate })=>{
        if (hasInSomeGroup({
            type,
            dirtyGroup,
            pristineGroup,
            notPresentGroup,
            fieldRecord,
            formFieldState
        }) && when(validationParams)) {
            return validate(validationParams);
        }
        return null;
    }));
/**
 * Function for validate all fields of given groups. Composed from two functions.
 *
 * @documented
 * @category ValidateFields
 * @import import validateFields from '@gef-ui/form-core/validations/validateFields'
 * @param {Object} input
 * @param {Object} input.groups
 * @param {String[]} [input.groups.dirtyGroup] - validation types for given group
 * @param {String[]} [input.groups.pristineGroup] - validation types for given group
 * @param {String[]} [input.groups.notPresentGroup] - validation types for given group
 * @param {Boolean} input.expandDependent - expand dependent fields for given fields
 * @param {Boolean} input.runType - FIELD or FORM validations
 * @param {Array[]} input.fieldsToValidate[]
 * @param {String} input.fieldsToValidate[][0].fieldName
 * @param {Number} [input.fieldsToValidate[][1].fieldIndices]
 * @param {Object} formData
 * @param {Object} formDescriptor
 * @param {Object} [formFieldState]
 * @param {Object} [reduxStore]
 * @returns {Object[]} errors
 * @returns {String} errors.type - validation type
 * @returns {String} errors.errorMessage
 * @returns {String} errors.fieldName
 * @returns {String} errors.section
 *
 */ const validateFields = ({ groups: { dirtyGroup = getValidationTypes(), pristineGroup = getPristineValidationTypes(), notPresentGroup = getNotPresentValidationTypes() } = {}, expandDependent = true, runType, fieldsToValidate, formData, formDescriptor, formFieldState = {}, reduxStore })=>{
    const resolvedValidationDescriptors = resolveValidationDescriptors({
        runType,
        formData,
        reduxStore,
        formDescriptor,
        formFieldState,
        expandDependent,
        fieldsToValidate
    });
    const result = {};
    for (const { when, validations, fieldRecord, validationParams, validateDependentPristine } of resolvedValidationDescriptors){
        const getValidationResults = createValidationResult({
            dirtyGroup,
            fieldRecord,
            pristineGroup: validateDependentPristine ? [
                ...pristineGroup,
                ...dirtyGroup
            ] : pristineGroup,
            formFieldState,
            notPresentGroup,
            validationParams
        });
        const valResult = getValidationResults(when(validationParams) && validations || []);
        result[fieldRecord] = o(expandWithFormState(formDescriptor, formFieldState), sortByPriority)(valResult);
    }
    return result;
};
/**
 * Function for running async validation for all fields of given groups. Composed from two functions.
 *
 * @documented
 * @category validateFieldsAsync
 * @import import validateFields from '@gef-ui/form-core/validations/validateFields'
 * @param {Object} input
 * @param {Object} input.groups
 * @param {String[]} [input.groups.dirtyGroup] - validation types for given group
 * @param {String[]} [input.groups.pristineGroup] - validation types for given group
 * @param {String[]} [input.groups.notPresentGroup] - validation types for given group
 * @param {Boolean} input.expandDependent - expand dependent fields for given fields
 * @param {Boolean} input.runType - FIELD or FORM validations
 * @param {Array[]} input.fieldsToValidate[]
 * @param {String} input.fieldsToValidate[][0].fieldName
 * @param {Number} [input.fieldsToValidate[][1].fieldIndices]
 * @param {Object} formData
 * @param {Object} formDescriptor
 * @param {Object} [formFieldState]
 * @param {Object} [reduxStore]
 * @returns {Object[]} errors
 * @returns {String} errors.type - validation type
 * @returns {String} errors.errorMessage
 * @returns {String} errors.fieldName
 * @returns {String} errors.section
 *
 */ export const validateFieldsAsync = async ({ groups: { dirtyGroup = getValidationTypes(), pristineGroup = getPristineValidationTypes(), notPresentGroup = getNotPresentValidationTypes() } = {}, expandDependent = true, runType, fieldsToValidate, formData, formDescriptor, formFieldState = {}, reduxStore })=>{
    const resolvedValidationDescriptors = resolveValidationDescriptors({
        runType,
        formData,
        reduxStore,
        formDescriptor,
        formFieldState,
        expandDependent,
        fieldsToValidate
    });
    return new Promise(async (resolve, reject)=>{
        const fieldRecordResults = await Promise.all(map(async ({ when, asyncValidations, fieldRecord, validationParams })=>{
            const getValidationResults = createValidationResult({
                dirtyGroup,
                fieldRecord,
                pristineGroup,
                formFieldState,
                notPresentGroup,
                validationParams
            });
            const asyncValResult = await Promise.all(getValidationResults(when(validationParams) && asyncValidations || [])).catch(reject);
            return [
                fieldRecord,
                compose(expandWithFormState(formDescriptor, formFieldState), sortByPriority, map(assoc('async', true)), rejectNil)(asyncValResult)
            ];
        }, resolvedValidationDescriptors)).catch(reject);
        const result = {};
        for (const [key, value] of fieldRecordResults){
            result[key] = value;
        }
        return resolve(result);
    });
};
export default validateFields;
